<template>
    <v-form
        ref="formAdditiveDialog"
        v-model="form.valid"
        lazy-validation
    >
        <v-dialog v-model="dialogState" persistent max-width="740px">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                >
                    {{ $t('common.add') }}
                    <span slot="loader" class="custom-loader">
                        <v-icon light>cached</v-icon>
                    </span>
                </v-btn>
            </template>
            <v-card v-if="dialogState">
                <v-card-title>
                    <span class="headline">{{ $t('contracts.form.additiveDialog.title') }}</span>
                </v-card-title>
                <v-card-text class="pb-1">
                    <v-row>
                        <v-col cols="12" sm="6" class="pt-1 pb-1">
                            <input-date-picker :setup.sync="form.datePickers.addendumDate"></input-date-picker>
                        </v-col>
                        <v-col cols="12" class="pt-1 pb-1">
                            <v-text-field
                                v-model="additive.additive"
                                :label="form.labels.additive"
                                :rules="form.rules.additive"
                                required
                                filled
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" class="pt-1 pb-4">
                            <div class="black--text"><strong>{{ form.labels.description }}</strong></div>
                            <v-row v-if="filePreview" class="d-flex justify-start">
                                <v-col>
                                    <contract-file-info :file="additive.file" :type="'observation'" :clearFile="clearFile"/>
                                </v-col>
                            </v-row>
                            <div v-else class="mb-5 text-center">
                                <span style="color: red">{{ $t('contracts.form.additiveDialog.empty') }}</span>
                            </div>
                            <editor-observation v-model="comment" :color="'#E0E0E0'" :background="true" :files="addFile" :multipleFiles="false"/>
                        </v-col>
                        <v-col cols="12" sm="7" class="pt-1 pb-1">
                            <template v-if="form.selects.status.selectedItems == 2">
                                <input-date-picker :setup.sync="form.datePickers.signatureDate"></input-date-picker>
                            </template>
                            <template v-if="form.selects.status.selectedItems == 3">
                                <input-date-picker :setup.sync="form.datePickers.cancellationDate"></input-date-picker>
                            </template>
                        </v-col>
                        <v-col cols="12" sm="5" class="pt-1 pb-1">
                            <input-select :setup.sync="form.selects.status"></input-select>
                        </v-col>
                        <template v-if="form.selects.status.selectedItems == 3">
                            <v-col cols="12" class="pt-1 pb-1">
                                <v-textarea
                                    v-model="additive.reasonCancellation"
                                    :label="form.labels.reasonCancellation"
                                    :rules="form.rules.reasonCancellation"
                                    required
                                    filled
                                    dense
                                    no-resize
                                ></v-textarea>
                            </v-col>
                        </template>
                    </v-row>
                </v-card-text>
                <v-card-actions class="pt-1 pl-6 pr-6">
                    <v-spacer></v-spacer>
                    <v-btn color="error" text @click="close()">{{ $t('common.close') }}</v-btn>
                    <v-btn color="primary" @click.native="save()">{{ $t('common.save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-form>
</template>

<script>
import InputDatePicker from '@/components/InputDatePicker'
import InputSelect from '@/components/InputSelect'
import ContractFileInfo from '@/components/contract/ContractFileInfo'
import EditorObservation from '@/components/EditorObservation'

export default {
    name: 'contract-additive-dialog',
    components: {
        InputDatePicker,
        InputSelect,
        ContractFileInfo,
        EditorObservation
    },
    props: ['show', 'additiveDetail', 'contract'],
    data () {
        return {
            dialogState: false,
            filePreview: false,
            changeFile: undefined,
            comment: null,
            form: {
                loading: true,
                valid: false,
                labels: {
                    additive: this.$t('contracts.form.additiveDialog.additive.label'),
                    description: this.$t('contracts.form.additiveDialog.description'),
                    reasonCancellation: this.$t('contracts.form.additiveDialog.reasonCancellation.label')
                },
                datePickers: {
                    addendumDate: {
                        label: this.$t('contracts.form.additiveDialog.addendumDate.label'),
                        rule: [
                            v => !!v || this.$t('contracts.form.additiveDialog.addendumDate.required')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined
                    },
                    signatureDate: {
                        label: this.$t('contracts.form.additiveDialog.signatureDate.label'),
                        rule: [
                            v => !!v || this.$t('contracts.form.additiveDialog.signatureDate.required')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined
                    },
                    cancellationDate: {
                        label: this.$t('contracts.form.additiveDialog.cancellationDate.label'),
                        rule: [
                            v => !!v || this.$t('contracts.form.additiveDialog.cancellationDate.required')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined
                    }
                },
                selects: {
                    status: {
                        label: this.$t('contracts.form.additiveDialog.status.label'),
                        text: 'label',
                        value: 'value',
                        icon: 'feedback',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('contracts.form.additiveDialog.status.required')
                        ],
                        items: [
                            { value: 1, label: this.$t('contracts.form.additiveDialog.status.items.pending') },
                            { value: 2, label: this.$t('contracts.form.additiveDialog.status.items.sign') },
                            { value: 3, label: this.$t('contracts.form.additiveDialog.status.items.canceled') }
                        ],
                        selectedItems: undefined
                    }
                },
                rules: {
                    additive: [
                        v => !!v || this.$t('contracts.form.additiveDialog.additive.required'),
                        v => (v && v.length >= 3) || this.$t('contracts.form.additiveDialog.additive.length')
                    ],
                    reasonCancellation: [
                        v => !!v || this.$t('contracts.form.additiveDialog.reasonCancellation.required')
                    ]
                }
            },
            additive: {
                objectId: undefined,
                additive: undefined,
                addendumDate: undefined,
                status: undefined,
                file: undefined,
                description: '',
                signatureDate: undefined,
                cancellationDate: undefined,
                reasonCancellation: undefined,
                changeFile: false
            }
        }
    },
    watch: {
        show: function (val, oldVal) {
            this.init()
            this.dialogState = val
        },
        dialogState: function (val, oldVal) {
            this.$emit('update:show', val)
            if (val === false) {
                this.$emit('update:additiveDetail', undefined)
            }
        }
    },
    methods: {
        init () {
            this.$refs.formAdditiveDialog.reset()
            if (this.additiveDetail !== undefined) {
                this.form.datePickers.addendumDate.dateFormatted = this.$options.filters.momentFormatDate(this.contract.additive[this.additiveDetail].addendumDate, 'L')
                if (this.contract.additive[this.additiveDetail].signatureDate) {
                    this.form.datePickers.signatureDate.dateFormatted = this.$options.filters.momentFormatDate(this.contract.additive[this.additiveDetail].signatureDate, 'L')
                }
                if (this.contract.additive[this.additiveDetail].cancellationDate) {
                    this.form.datePickers.cancellationDate.dateFormatted = this.$options.filters.momentFormatDate(this.contract.additive[this.additiveDetail].cancellationDate, 'L')
                }
                this.additive = Object.assign({}, this.contract.additive[this.additiveDetail])
                this.filePreview = !!(this.additive.file)
                this.comment = this.additive.description
                this.form.selects.status.selectedItems = this.additive.status
            }
        },
        async addFile (event) {
            for (let i = 0; event.target.files.length > i; i++) {
                const file = event.target.files[i]
                const tempFile = file.slice(0, file.size)
                const newFile = new File([tempFile], file.name.replace(/[^a-zA-Z._0-9]/g, ''))
                this.additive.changeFile = true
                this.filePreview = true
                this.additive.file = newFile
            }
        },
        clearFile () {
            this.additive.changeFile = true
            this.filePreview = false
            this.additive.file = undefined
        },
        formatObject () {
            this.additive.addendumDate = this.$helpers.formatDatetoParse(this.form.datePickers.addendumDate.dateFormatted)
            this.additive.status = this.form.selects.status.selectedItems

            if (this.additive.status === 3) {
                this.additive.cancellationDate = this.$helpers.formatDatetoParse(this.form.datePickers.cancellationDate.dateFormatted)
            } else if (this.additive.status === 2) {
                this.additive.signatureDate = this.$helpers.formatDatetoParse(this.form.datePickers.signatureDate.dateFormatted)
            } else {
                this.additive.cancellationDate = { __op: 'Delete' }
                this.additive.signatureDate = { __op: 'Delete' }
            }
            this.additive.description = this.comment
            return this.additive
        },
        save () {
            if (!this.$refs.formAdditiveDialog.validate()) {
                return
            }
            if (this.additiveDetail !== undefined) {
                this.contract.additive[this.additiveDetail] = Object.assign({}, this.formatObject())
            } else {
                this.contract.additive.push(Object.assign({}, this.formatObject()))
            }
            window.getApp.refreshContracts = true
            this.close()
        },
        close () {
            this.comment = null
            this.clearFile()
            this.$forceUpdate()
            this.$emit('close-additive-dialog')
            this.dialogState = false
        }
    }
}
</script>

<style lang="stylus" scoped>
</style>
